import React, { useState, useRef, useEffect } from "react";
import "./Categories.css";
import {
  FiSearch,
  FiPlus,
  FiFilter,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiUploadCloud,
  FiX,
  FiCheck,
  FiChevronRight,
  FiRefreshCw,
} from "react-icons/fi";
import { BiExport } from "react-icons/bi";

const API_BASE = process.env.REACT_APP_EP || "http://localhost:5000";

const emptyFormState = {
  id: null,
  name: "",
  description: "",
  imageUrl: "",
  imageFile: null,
  added: null,
  sales: 0,
  stock: 0,
};

// Helper to extract error message from API response
const extractErrorMessage = (data) => {
  if (!data) return "An error occurred";
  if (typeof data === "string") return data;

  // Handle errors array
  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors.map((err) => err.msg || err.message || JSON.stringify(err)).join(" | ");
  }

  if (data.message) return data.message;
  if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);

  return JSON.stringify(data);
};

const Categories = () => {
  const [mode, setMode] = useState("table");
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState(emptyFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const pageSize = 10;

  // --------- FETCH CATEGORIES ---------
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errData) || "Failed to fetch categories");
      }

      const data = await response.json();
      console.log("Fetched categories:", data);

      // Your API returns: { success: true, data: [...] }
      const categoriesList = data.data || data.categories || data || [];

      // Transform using database column names
      // In fetchCategories function
const transformed = categoriesList.map((cat) => ({
  id: cat?.category_id || cat?.id || cat?._id || "",
  name: cat?.category_name || cat?.name || "",
  description: cat?.description || "",
  imageUrl: cat?.image_url || cat?.imageUrl || "",
  added: cat?.created_at || cat?.createdAt || null,
  // ✅ Parse as integers and use correct field names from API
  sales: parseInt(cat?.sales) || 0,
  stock: parseInt(cat?.stock) || 0,
  productCount: parseInt(cat?.product_count) || 0,
}));

      setCategories(transformed);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // --------- CREATE CATEGORY ---------
  const createCategory = async (categoryData) => {
    setSaving(true);
    setError(null);

    try {
      const formDataToSend = new FormData();

      // Use database column name: category_name
      formDataToSend.append("category_name", (categoryData.name || "").trim());
      formDataToSend.append("description", categoryData.description || "");

      if (categoryData.imageFile) {
        formDataToSend.append("image", categoryData.imageFile);
      }

      // Debug log
      console.log("=== Sending Category Data ===");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await fetch(`${API_BASE}/api/categories`, {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();
      console.log("Create response:", data);

      if (!response.ok) {
        throw new Error(extractErrorMessage(data));
      }

      const newCategory = data.data || data.category || data;

      const transformed = {
        id: newCategory?.category_id || newCategory?.id || Date.now(),
        name: newCategory?.category_name || newCategory?.name || categoryData.name || "",
        description: newCategory?.description || categoryData.description || "",
        imageUrl: newCategory?.image_url || newCategory?.imageUrl || "",
        added: newCategory?.created_at || new Date().toISOString(),
        sales: 0,
        stock: 0,
      };

      setCategories((prev) => [transformed, ...prev]);
      return transformed;
    } catch (err) {
      console.error("Create category error:", err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // --------- UPDATE CATEGORY ---------
  const updateCategory = async (id, categoryData) => {
    setSaving(true);
    setError(null);

    try {
      const formDataToSend = new FormData();

      // Use database column name: category_name
      formDataToSend.append("category_name", (categoryData.name || "").trim());
      formDataToSend.append("description", categoryData.description || "");

      if (categoryData.imageFile) {
        formDataToSend.append("image", categoryData.imageFile);
      }

      const response = await fetch(`${API_BASE}/api/categories/${id}`, {
        method: "PUT",
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(extractErrorMessage(data));
      }

      const updatedCategory = data.data || data.category || data;

      const transformed = {
        id: updatedCategory?.category_id || updatedCategory?.id || id,
        name: updatedCategory?.category_name || updatedCategory?.name || categoryData.name || "",
        description: updatedCategory?.description || categoryData.description || "",
        imageUrl: updatedCategory?.image_url || updatedCategory?.imageUrl || categoryData.imageUrl || "",
        added: updatedCategory?.created_at || null,
        sales: 0,
        stock: 0,
      };

      setCategories((prev) =>
        prev.map((cat) => (cat.id === id ? transformed : cat))
      );

      return transformed;
    } catch (err) {
      console.error("Update category error:", err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // --------- DELETE CATEGORY ---------
  const deleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/categories/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(data) || "Failed to delete category");
      }

      setCategories((prev) => prev.filter((cat) => cat.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --------- DELETE CATEGORY IMAGE ---------
  const deleteCategoryImage = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/api/categories/${id}/image`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to delete image");
      }

      setCategories((prev) =>
        prev.map((cat) => (cat.id === id ? { ...cat, imageUrl: "" } : cat))
      );

      if (formData.id === id) {
        setFormData((prev) => ({ ...prev, imageUrl: "", imageFile: null }));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ---------- HELPERS ----------
  const filteredCategories = categories.filter((category) => {
    if (!category) return false;

    const term = (searchTerm || "").toLowerCase();
    const name = (category.name || "").toLowerCase();
    const description = (category.description || "").toLowerCase();

    return name.includes(term) || description.includes(term);
  });

  const totalItems = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const currentItems = filteredCategories.slice(startIndex, startIndex + pageSize);

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    return `${API_BASE}${imageUrl}`;
  };

  // ---------- TABLE HANDLERS ----------
  const handleAddCategoryClick = () => {
    setFormData(emptyFormState);
    setIsEditing(false);
    setFormError("");
    setMode("form");
  };

  const handleEditCategory = (category) => {
    setFormData({
      id: category?.id || null,
      name: category?.name || "",
      description: category?.description || "",
      imageUrl: category?.imageUrl || "",
      imageFile: null,
      added: category?.added || null,
      sales: category?.sales || 0,
      stock: category?.stock || 0,
    });
    setIsEditing(true);
    setFormError("");
    setMode("form");
  };

  const handleDeleteCategory = (id) => {
    deleteCategory(id);
  };

  const handleChangePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => handleChangePage(safePage - 1);
  const handleNextPage = () => handleChangePage(safePage + 1);

  // ---------- FORM HANDLERS ----------
  const handleCancelForm = () => {
    setMode("table");
    setIsEditing(false);
    setFormData(emptyFormState);
    setFormError("");
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.name.trim()) {
      setFormError("Category name is required.");
      return;
    }
    setFormError("");

    try {
      if (isEditing && formData.id) {
        await updateCategory(formData.id, formData);
      } else {
        await createCategory(formData);
        setCurrentPage(1);
      }

      setMode("table");
      setIsEditing(false);
      setFormData(emptyFormState);
    } catch (err) {
      setFormError(err.message || "Failed to save category");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      imageUrl: previewUrl,
      imageFile: file,
    }));
  };

  const handleRemoveImage = () => {
    if (isEditing && formData.id && formData.imageUrl && !formData.imageFile) {
      if (window.confirm("Remove this image from the category?")) {
        deleteCategoryImage(formData.id);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        imageUrl: "",
        imageFile: null,
      }));
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleExport = () => {
    const headers = ["Name", "Description", "Added"];
    const rows = categories.map((cat) => [
      cat?.name || "",
      cat?.description || "",
      formatDate(cat?.added),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `categories-export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ---------- RENDER ----------
  return (
    <div className="categories-page">
      {/* Error Toast */}
      {error && (
        <div className="error-toast">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {mode === "table" ? (
        <>
          {/* TABLE MODE HEADER */}
          <div className="categories-header">
            <div className="categories-header-top">
              <div className="categories-header-left">
                <h1 className="categories-title">Categories</h1>
                <div className="categories-breadcrumbs">
                  <span className="breadcrumb-link">Dashboard</span>
                  <FiChevronRight className="breadcrumb-separator-icon" />
                  <span className="breadcrumb-current">Categories</span>
                </div>
              </div>

              <div className="categories-header-right">
                <button
                  className="btn btn-icon-only"
                  type="button"
                  onClick={fetchCategories}
                  disabled={loading}
                  title="Refresh"
                >
                  <FiRefreshCw className={`btn-icon ${loading ? "spinning" : ""}`} />
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleExport}
                  disabled={loading || categories.length === 0}
                >
                  <BiExport className="btn-icon" />
                  <span>Export</span>
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleAddCategoryClick}
                  disabled={loading}
                >
                  <FiPlus className="btn-icon" />
                  <span>Add Category</span>
                </button>
              </div>
            </div>

            <div className="categories-header-bottom">
              <div className="categories-search-wrapper">
                <FiSearch className="categories-search-icon" />
                <input
                  type="text"
                  className="categories-search-input"
                  placeholder="Search category..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="categories-filters-right">
                <button
                  type="button"
                  className="filters-btn"
                  onClick={() => console.log("Filters clicked")}
                >
                  <FiFilter className="filters-icon" />
                  <span>Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* TABLE CARD */}
          <div className="categories-card">
            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <span>Loading categories...</span>
              </div>
            )}

            <div className="categories-table-wrapper">
              <table className="categories-table">
                <thead>
                  <tr>
                    <th className="col-category">
                      <div className="category-header-cell">
                        <input type="checkbox" />
                        <span>Category</span>
                      </div>
                    </th>
                    <th className="col-number">
                      <span>Sales</span>
                    </th>
                    <th className="col-number">
                      <span>Stock</span>
                    </th>
                    <th className="col-added">
                      <span>Added</span>
                    </th>
                    <th className="col-action">
                      <span>Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && totalItems === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-table-cell">
                        No categories yet. Click <strong>Add Category</strong> to create
                        one.
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((category) => (
                      <tr key={category.id}>
                        <td className="col-category" data-label="Category">
                          <div className="category-cell">
                            <input type="checkbox" className="row-checkbox" />
                            <div className="category-thumbnail">
                              {category.imageUrl ? (
                                <img
                                  src={getImageUrl(category.imageUrl)}
                                  alt={category.name || "Category"}
                                />
                              ) : (
                                <div className="thumbnail-placeholder" />
                              )}
                            </div>
                            <div className="category-info">
                              <div className="category-name">{category.name || "-"}</div>
                              <div className="category-description">
                                {category.description || ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="col-number" data-label="Sales">
                          {(category.sales || 0).toLocaleString()}
                        </td>
                        <td className="col-number" data-label="Stock">
                          {(category.stock || 0).toLocaleString()}
                        </td>
                        <td className="col-added" data-label="Added">
          {formatDate(category.added)}
        </td>
        <td className="col-action" data-label="Action">
                          <div className="action-buttons1">
                            <button
                              type="button"
                              className="icon-button small"
                              title="View"
                              onClick={() => console.log("View category", category.id)}
                            >
                              <FiEye />
                            </button>
                            <button
                              type="button"
                              className="icon-button small"
                              title="Edit"
                              onClick={() => handleEditCategory(category)}
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="icon-button small danger"
                              title="Delete"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalItems > 0 && (
              <div className="categories-pagination">
                <div className="pagination-info">
                  {`Showing ${startIndex + 1}-${Math.min(
                    startIndex + pageSize,
                    totalItems
                  )} of ${totalItems}`}
                </div>
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="page-button"
                    onClick={handlePrevPage}
                    disabled={safePage === 1}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const page = index + 1;
                    return (
                      <button
                        type="button"
                        key={page}
                        className={`page-button ${page === safePage ? "active" : ""}`}
                        onClick={() => handleChangePage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="page-button"
                    onClick={handleNextPage}
                    disabled={safePage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* FORM MODE HEADER */}
          <div className="categories-header">
            <div className="categories-header-top">
              <div className="categories-header-left">
                <h1 className="categories-title">
                  {isEditing ? "Edit Category" : "Add Category"}
                </h1>
                <div className="categories-breadcrumbs">
                  <span className="breadcrumb-link">Dashboard</span>
                  <FiChevronRight className="breadcrumb-separator-icon" />
                  <button
                    type="button"
                    className="breadcrumb-link breadcrumb-button"
                    onClick={handleCancelForm}
                  >
                    Categories
                  </button>
                  <FiChevronRight className="breadcrumb-separator-icon" />
                  <span className="breadcrumb-current">
                    {isEditing ? "Edit Category" : "Add Category"}
                  </span>
                </div>
              </div>

              <div className="categories-header-right">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleCancelForm}
                  disabled={saving}
                >
                  <FiX className="btn-icon" />
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  form="category-form"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  <FiCheck className="btn-icon" />
                  <span>{saving ? "Saving..." : "Save Category"}</span>
                </button>
              </div>
            </div>
          </div>

          {/* FORM */}
          <form
            id="category-form"
            className="category-form"
            onSubmit={handleSaveCategory}
          >
            <div className="category-form-layout">
              {/* THUMBNAIL CARD */}
              <div className="card thumbnail-card">
                <h2 className="card-title">Thumbnail</h2>
                <div className="field-label">Photo</div>

                <div className="thumbnail-dropzone" onClick={openFilePicker}>
                  {formData.imageUrl ? (
                    <div className="thumbnail-preview">
                      <img src={getImageUrl(formData.imageUrl)} alt="Category" />
                      <span className="thumbnail-check">✓</span>
                    </div>
                  ) : (
                    <div className="thumbnail-empty">
                      <div className="thumbnail-empty-box" />
                      <p className="thumbnail-empty-text">
                        Drag and drop image here, or click to add image
                      </p>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </div>

                <div className="thumbnail-actions">
                  <button
                    type="button"
                    className="btn btn-light full-width"
                    onClick={openFilePicker}
                  >
                    <FiUploadCloud className="btn-icon" />
                    <span>{formData.imageUrl ? "Change Image" : "Add Image"}</span>
                  </button>

                  {formData.imageUrl && (
                    <button
                      type="button"
                      className="btn btn-danger-outline full-width"
                      onClick={handleRemoveImage}
                    >
                      <FiTrash2 className="btn-icon" />
                      <span>Remove Image</span>
                    </button>
                  )}
                </div>
              </div>

              {/* GENERAL INFO CARD */}
              <div className="card general-card">
                <h2 className="card-title">General Information</h2>

                <div className="form-group">
                  <label className="field-label">
                    Category Name <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    className={`text-input ${formError ? "input-error" : ""}`}
                    placeholder="Enter category name"
                    value={formData.name || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  {formError && <div className="validation-error">{formError}</div>}
                </div>

                <div className="form-group">
                  <label className="field-label">Description</label>
                  <textarea
                    rows={6}
                    className="text-area"
                    placeholder="Write a short description..."
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Categories;